 
import {useMemo, useRef} from "react";
import {connect, ConnectedProps} from "react-redux";
import {DirectionalHint, Stack, TooltipHost} from "@fluentui/react";
import {Tag} from "@fluentui/react-components";
import DOMPurify from "dompurify";
import {initializeIcons} from "@fluentui/react";
import styles from "./Answer.module.css";
import {ArrowClockwiseRegular, Receipt24Regular} from "@fluentui/react-icons";
import {Button as FluentUIButton} from "@fluentui/react-components";
import {RAGChatCompletion} from "../../api/models";
import {parseAnswerToHtml} from "./AnswerParser";
import {AnswerIcon} from "./AnswerIcon";
import {DiagnosisReport} from "./DiagnosisReport";
import {DataTable} from "@/components/DataTable";
import DataTableMetricValueDisplayElement from "@/components/DataTable/DataTableMetricValueDisplayElement";
import {
    BusinessUnitType,
    CustomColumnMetadataFunctionType,
    IBusinessUnit,
    IColumnMetadata,
    ICustomColumnsTransformation,
    IDataTableData
} from "@/features/application/api/applicationModels";
import {openTicket} from "@/features/ticket/TicketModalService";
import {Button, Table} from "semantic-ui-react";
import {AppState} from "@/state/store";
import {MarkdownViewer} from "../MarkdownViewer";
import {FeedbackButtons} from "../FeedbackButtons/FeedbackButtons";
import EnhancedChartComponent from "../Chart/EnhancedChart";
import ExportExcel from "@/components/ExportExcel";

interface IProps {
    answer: RAGChatCompletion;
    isStreaming: boolean;
    onFollowupQuestionClicked?: (question: string) => void;
    onRetryClicked?: () => void;
    showFollowupQuestions?: boolean;
    isRequestActive?: boolean;
    promptText?: string;
    userId?: string;
}

type AnswerProps = IReduxProps & IProps;

//Todo: This shouldn't really be here, should be in the index.tsx of the whole app
initializeIcons();

const AnswerComponent = ({
    answer,
    isStreaming,
    onFollowupQuestionClicked,
    onRetryClicked,
    isRequestActive = false,
    legacySiteBaseUrl,
    clientTreeHierarchy,
    clientConcept,
    promptText,
    userId
}: AnswerProps) => {
    const exportRef = useRef<ExportExcel>(null);
    const followupQuestions = answer.context.followup_questions;
    const messageContent = answer.message.content;
    const highchart = answer.context.highchart_data;
    const filename = answer.context.file_name;
    const parsedAnswer = useMemo(
        () => parseAnswerToHtml(messageContent, isStreaming),
        [isStreaming, messageContent]
    );
    const dataPoints = answer.context.data_points;
    const diagnosisReport = answer.context.diagnosis_report;
    const columnsMetadata: IColumnMetadata[] = useMemo(() => {
        const allColumns =
            answer.context.data_points_metadata?.columns_metadata || [];

        // If TicketNbr is present, use the ordering as returned by the backend
        // This is because the presence of TicketNbr indicates that the data was not aggregated
        // And the order is enforced in the back end.
        if (allColumns.some((col) => (col.key as string) === "TicketNbr")) {
            return allColumns;
        }

        // Define priority columns to be moved to the front
        const priorityColumns = [
            "BusinessUnit",
            "BusDt",
            "FromDate",
            "ToDate",
            "DateRangeLabel",
            "DayOfWeek"
        ];

        // Separate priority columns from other columns
        const priorityFound: IColumnMetadata[] = [];
        const otherColumns: IColumnMetadata[] = [];

        allColumns.forEach((column) => {
            const columnKey = column.key as string;
            if (priorityColumns.includes(columnKey)) {
                priorityFound.push(column);
            } else {
                otherColumns.push(column);
            }
        });

        // Sort priority columns based on the order in priorityColumns array
        priorityFound.sort((a, b) => {
            return (
                priorityColumns.indexOf(a.key as string) -
                priorityColumns.indexOf(b.key as string)
            );
        });

        // Combine priority columns with other columns
        return [...priorityFound, ...otherColumns];
    }, [answer.context.data_points_metadata?.columns_metadata]);

    const sortColumn = answer.context.data_points_metadata?.sort_column;
    const sortDirection = answer.context.data_points_metadata?.sort_direction;

    const tableData: IDataTableData[] = useMemo(() => {
        if (!dataPoints) return [];
        const entries = Object.entries(dataPoints);

        // Extract just the second element of each entry, which is the data object itself
        return entries.map(([, dataObject]) => {
            const row: IDataTableData = dataObject;
            return row;
        });
    }, [dataPoints]);

    // Check if we have valid data for displaying the table
    const hasValidTableData = useMemo(() => {
        return (
            tableData &&
            tableData.length > 0 &&
            columnsMetadata &&
            columnsMetadata.length > 0
        );
    }, [tableData, columnsMetadata]);
    const getCustomTicketColumn: CustomColumnMetadataFunctionType = (
        dataTableRow: IDataTableData,
        column: IColumnMetadata,
        isMobileScreen: boolean
    ) => {
        return {
            element: (
                <Table.Cell
                    key={
                        "Cellchecknumber" +
                        dataTableRow.BusDt +
                        dataTableRow.Loc_PK +
                        dataTableRow.TicketNbr
                    }
                >
                    <DataTableMetricValueDisplayElement
                        isMobileScreen={isMobileScreen}
                        metricName={column.label}
                        metricDisplayValue1={
                            <Button
                                onClick={() =>
                                    dataTableRow.BusDt &&
                                    dataTableRow.Loc_PK &&
                                    dataTableRow.TicketNbr
                                        ? openTicket(
                                              String(dataTableRow.BusDt),
                                              Number(dataTableRow.Loc_PK),
                                              Number(dataTableRow.TicketNbr),
                                              clientConcept.concept,
                                              legacySiteBaseUrl
                                          )
                                        : null
                                }
                                className="ticket-receipt-btn"
                            >
                                <Receipt24Regular className="ticket-receipt-icon" />
                                {dataTableRow.OrderNumber}
                            </Button>
                        }
                    />
                </Table.Cell>
            ),
            sortValue: dataTableRow.OrderNumber
        };
    };

    // kind of a by product but this will only show if the OrderNumber is in the data.
    const customColumns: ICustomColumnsTransformation = {
        OrderNumber: {
            getCustomColumnMetaData: getCustomTicketColumn
        }
    };

    const sanitizedAnswerHtml = DOMPurify.sanitize(parsedAnswer.answerHtml);

    // The backend signals text-stream completion via context.text_stream_complete.
    // Once true, charts/tables can render without waiting for follow-up questions.
    const hasEndMarker = answer.context.text_stream_complete === true;

    const getBusinessUnitName = (bu: IBusinessUnit): string => {
        const displayName =
            bu.businessUnitType === BusinessUnitType.Location &&
            bu.businessUnitDescription // businessUnitDescription can sometimes be null
                ? bu.businessUnitName + " - " + bu.businessUnitDescription
                : bu.businessUnitName;

        return displayName || "Unknown Business Unit";
    };

    // Get business unit from answer context
    const answerBusinessUnit = clientTreeHierarchy.find(
        (bu) => bu.businessUnitId === answer.context.currentSelectedBusinessUnitId
    );

    const handleDownloadClick = () => {
        if (exportRef.current) {
            exportRef.current.handleExportClick();
        }
    };

    return (
        <Stack className={`${styles.answerContainer}`} verticalAlign="space-between">
            <Stack.Item>
                <Stack horizontal horizontalAlign="space-between">
                    <AnswerIcon />
                </Stack>
            </Stack.Item>
            <Stack.Item>
                {answerBusinessUnit && (
                    <div style={{marginTop: "10px", marginBottom: "10px"}}>
                        <Tag
                            appearance="brand"
                            shape="rounded"
                            size="medium"
                            style={{
                                backgroundColor: "rgba(115, 118, 225, 0.1)",
                                color: "rgba(115, 118, 225, 1)",
                                border: "1px solid rgba(115, 118, 225, 0.3)",
                                borderRadius: "20px",
                                padding: "6px 12px"
                            }}
                        >
                            {getBusinessUnitName(answerBusinessUnit)}
                        </Tag>
                    </div>
                )}
            </Stack.Item>
            {/* Text Response */}
            <Stack.Item grow>
                <div className={styles.answerText}>
                    <MarkdownViewer text={sanitizedAnswerHtml} />
                </div>
            </Stack.Item>
            {/* Diagnosis report if present */}
            {hasEndMarker && diagnosisReport && (
                <Stack.Item>
                    <DiagnosisReport report={diagnosisReport} />
                </Stack.Item>
            )}
            {/* Chart if present */}
            {hasEndMarker && highchart && hasValidTableData && (
                <Stack.Item>
                    <div className={styles.chartContainer}>
                        <EnhancedChartComponent
                            highchartConfig={highchart}
                            data={tableData}
                            columnsMetadata={columnsMetadata}
                        />
                    </div>
                </Stack.Item>
            )}
            {/* Data Table if present */}
            {hasEndMarker && hasValidTableData && (
                <Stack.Item>
                    <DataTable
                        data={tableData}
                        staticColumns={columnsMetadata}
                        customColumns={customColumns}
                        sortColumnKey={sortColumn}
                        sortDirection={sortDirection}
                        itemsPerPage={10}
                        hideNoResultsMessage={true}
                        screenWidth={800}
                        retainActivePage={true}
                        exportFileName={filename || "coach-ai-data"}
                    />
                </Stack.Item>
            )}
            {hasEndMarker && filename && hasValidTableData && (
                <>
                    <Stack.Item>
                        <a
                            className={styles.downloadLink}
                            onClick={handleDownloadClick}
                            style={{cursor: "pointer"}}
                        >
                            Download
                        </a>
                    </Stack.Item>
                    <div style={{display: "none"}}>
                        <ExportExcel
                            ref={exportRef}
                            data={tableData}
                            columns={columnsMetadata}
                            fileName={filename || "coach-ai-data"}
                        />
                    </div>
                </>
            )}

            {followupQuestions &&
                followupQuestions?.length > 0 &&
                onFollowupQuestionClicked && (
                    <Stack.Item styles={{root: {marginTop: 20}}}>
                        <span className={styles.followupQuestionLearnMore}>
                            Follow-up questions:
                        </span>
                        <Stack
                            tokens={{childrenGap: 6}}
                            styles={{root: {marginTop: 6}}}
                        >
                            {followupQuestions.map((x, i) => {
                                return (
                                    <div key={i}>
                                        <Button
                                            className={styles.followupQuestion}
                                            title={x}
                                            onClick={() =>
                                                onFollowupQuestionClicked(x)
                                            }
                                            type="button"
                                        >
                                            {`${x}`}
                                        </Button>
                                    </div>
                                );
                            })}
                        </Stack>
                    </Stack.Item>
                )}
            {onRetryClicked && !isStreaming && (
                <Stack.Item styles={{root: {marginTop: 10}}}>
                    <Stack horizontal horizontalAlign="start">
                        {promptText && userId && (
                            <Stack.Item styles={{root: {marginTop: 5}}}>
                                <FeedbackButtons
                                    businessUnitId={
                                        answer.context
                                            .currentSelectedBusinessUnitId || ""
                                    }
                                    promptText={promptText}
                                    responseText={messageContent}
                                    datadogTraceId={
                                        answer.context.datadog_trace_id ?? ""
                                    }
                                />
                            </Stack.Item>
                        )}
                        <TooltipHost
                            content="Retry this question"
                            directionalHint={DirectionalHint.bottomCenter}
                        >
                            <FluentUIButton
                                size="large"
                                icon={<ArrowClockwiseRegular />}
                                onClick={onRetryClicked}
                                disabled={isRequestActive}
                                className={`${styles.retryButton}${
                                    isRequestActive ? " disabled-opacity" : ""
                                }`}
                            />
                        </TooltipHost>
                    </Stack>
                </Stack.Item>
            )}
        </Stack>
    );
};

const mapStateToProps = (state: AppState) => ({
    legacySiteBaseUrl: state.application.legacySiteBaseUrl,
    clientTreeHierarchy: state.application.clientTreeHierarchy,
    clientConcept: state.application.selectedClientConcept
});

const connector = connect(mapStateToProps);

type IReduxProps = ConnectedProps<typeof connector>;
export const Answer = connector(AnswerComponent);
